// mosaic — the framework-agnostic Mosaic pipeline (grid + timeline + plan).
//
// A port of the device-served `web/mosaic.js` Mosaic, **upgraded** to run in the
// hosted webapp's secure context (ADR-0008's amendment, ADR-0017/0022). The
// deterministic logic that decides the OUTPUT — grid sizing, the per-clip
// real-time timeline (which source frame plays at output time `t`, via each
// clip's GCE delays), the photo-as-static-cell, and the output-frame plan — is
// split out here as pure functions so the proof is unit-tested WITHOUT a canvas
// or a codec. The DOM/canvas reconstruction (frameToCell) and the WebCodecs
// encode (encodeMosaic.ts) are thin wrappers over this math.
//
// Reuses B3's gifDecode core: a Clip is a list of GifFrame (indices + palette +
// `delaySeconds`); the Mosaic loops each clip at its true recorded speed off
// those delays, exactly as web/mosaic.js did off `(pend||10)/100`.

/**
 * The minimal per-frame shape the real-time timeline math actually reads: just each
 * frame's display delay. A full `GifFrame` satisfies it, and so does a bare timing
 * list (e.g. a Look preview's per-frame delays), so callers can drive `frameForTime`
 * / `clipDuration` WITHOUT fabricating partial `GifFrame`s behind an unsafe cast (R28).
 */
export type FrameTiming = { readonly delaySeconds: number };

/** Native cell size in px — 240² Captures/Clips, drawn 1:1 (no downscale). */
export const MOSAIC_CELL = 240;
/** Output frame rate (web/mosaic.js: FPS = 12). */
export const MOSAIC_FPS = 12;
/** Tile cap — the contact-Sheet's cap (web/mosaic.js: MAXSEL = 32). */
export const MOSAIC_MAX_SELECTION = 32;
/** Fallback duration (s) when every cell is a static photo (web/mosaic.js: 3). */
export const MOSAIC_STATIC_SECONDS = 3;
/**
 * Output length (s) the loop count defaults to. Instagram refuses a GIF but takes an
 * MP4, and ~30 s is a comfortable post — so a Mosaic (even of one photo) leaves as a
 * postable video by default instead of a single sub-second loop.
 */
export const MOSAIC_TARGET_SECONDS = 30;
/** Longest output the length stepper reaches — bounds the encode (120 s × 12 fps = 1440 frames). */
export const MOSAIC_MAX_SECONDS = 120;

/** The two layouts web/mosaic.js offers (mirrors the Sheet's square/strip). */
export type MosaicLayout = "square" | "vertical";

/** Grid geometry: the column/row count, the total cells, and the pixel size. */
export interface MosaicGrid {
  readonly cols: number;
  readonly rows: number;
  /** cols*rows — may exceed the selection count (the last row loops). */
  readonly cells: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The grid shape for `count` cells under a layout — ported verbatim from
 * web/mosaic.js:
 *
 *   square   → cols = ceil(sqrt(count))            (≈ square, e.g. 16 → 4×4)
 *   vertical → cols = round(sqrt(count / 2))       (fewer cols ⇒ taller ⇒ ~1:2)
 *   rows = ceil(count / cols)
 *
 * `colsOverride` (the UI's column stepper) replaces the layout heuristic with an
 * explicit width, clamped to `[1, count]` so it always yields a sane rectangle —
 * at least one column, at most one cell per column (a single row). That's what
 * lets the owner dial e.g. 12 cells to a 3×4 instead of the vertical default 2×6.
 *
 * Cells are native 240px, so every output dimension is a multiple of 16
 * (H.264-friendly). The last partial row is filled by looping the cells
 * (`i % count`), so `cols*rows` can exceed `count`.
 */
export function planGrid(
  count: number,
  layout: MosaicLayout,
  colsOverride?: number,
): MosaicGrid {
  const n = Math.max(1, Math.floor(count));
  const autoCols =
    layout === "vertical"
      ? Math.max(1, Math.round(Math.sqrt(n / 2)))
      : Math.max(1, Math.ceil(Math.sqrt(n)));
  const cols =
    colsOverride != null && Number.isFinite(colsOverride)
      ? Math.min(n, Math.max(1, Math.round(colsOverride)))
      : autoCols;
  const rows = Math.ceil(n / cols);
  return {
    cols,
    rows,
    cells: cols * rows,
    width: cols * MOSAIC_CELL,
    height: rows * MOSAIC_CELL,
  };
}

/**
 * The top-left pixel position of grid slot `i` (row-major), in a `cols`-wide grid
 * of native 240px cells. Pure geometry, so the encode loop is trivial.
 */
export function cellPosition(i: number, cols: number): { x: number; y: number } {
  return {
    x: (i % cols) * MOSAIC_CELL,
    y: Math.floor(i / cols) * MOSAIC_CELL,
  };
}

/**
 * A Clip's total loop duration in seconds — the sum of its per-frame GCE delays
 * (web/mosaic.js: `dur += frames[i].delay`, clamped to ≥ 1s so a degenerate
 * clip with no delays still advances). A photo (no frames) has duration 0.
 */
export function clipDuration(frames: readonly FrameTiming[]): number {
  if (frames.length === 0) return 0;
  let dur = 0;
  for (const f of frames) dur += f.delaySeconds;
  return dur <= 0 ? 1 : dur;
}

/**
 * The Mosaic's BASE duration (s) — one loop: the **longest** clip in the selection
 * (web/mosaic.js: `dur = max(dur, cells[i].dur)`), or MOSAIC_STATIC_SECONDS when
 * the selection is all photos (every duration 0). The output is a whole number of
 * these loops (`planMosaic`'s `loops`), so the longest clip always ends on a boundary.
 */
export function mosaicDuration(durations: readonly number[]): number {
  let dur = 0;
  for (const d of durations) dur = Math.max(dur, d);
  return dur > 0 ? dur : MOSAIC_STATIC_SECONDS;
}

/**
 * How many loops of a `baseSeconds`-long Mosaic land NEAREST `targetSeconds` (≥ 1) —
 * the length stepper's default. Whole loops only, so a 4.2 s clip gives 7 loops =
 * 29.4 s and a 3 s all-photo grid gives 10 loops = 30 s. A base that is not a positive
 * finite number (unknown / degenerate) yields a single loop.
 */
export function loopsForTarget(
  baseSeconds: number,
  targetSeconds: number = MOSAIC_TARGET_SECONDS,
): number {
  if (!Number.isFinite(baseSeconds) || baseSeconds <= 0) return 1;
  return Math.max(1, Math.round(targetSeconds / baseSeconds));
}

/**
 * The most loops of `baseSeconds` that fit in `maxSeconds` — the stepper's ceiling.
 * Never below 1: a single loop is always allowed, even for a clip longer than the cap.
 */
export function maxLoops(
  baseSeconds: number,
  maxSeconds: number = MOSAIC_MAX_SECONDS,
): number {
  if (!Number.isFinite(baseSeconds) || baseSeconds <= 0) return 1;
  return Math.max(1, Math.floor(maxSeconds / baseSeconds));
}

/**
 * Number of output frames for a Mosaic of `durationSeconds` at `fps`
 * (web/mosaic.js: `max(1, round(dur * FPS))`).
 */
export function outputFrameCount(
  durationSeconds: number,
  fps: number = MOSAIC_FPS,
): number {
  return Math.max(1, Math.round(durationSeconds * fps));
}

/**
 * Which source frame of a clip is showing at output time `t` seconds — the
 * per-clip REAL-TIME timeline. The clip loops every `duration` seconds; within a
 * loop we walk the per-frame delays to find the frame whose interval contains the
 * elapsed time. This is web/mosaic.js's `drawCell` selection, lifted out pure:
 *
 *   acc = t % duration
 *   j = 0; while (j < last && acc >= delay[j]) { acc -= delay[j]; j++ }
 *
 * so each cell plays at its TRUE recorded speed (faithful motion over a forced
 * common rate — the project's real-time/honest bias), independent of the others.
 *
 * @returns the source-frame index in [0, frames.length-1]; 0 for an empty/1-frame
 *          clip (a photo cell is handled by the caller as a single static frame).
 */
export function frameForTime(frames: readonly FrameTiming[], t: number): number {
  const length = frames.length;
  if (length <= 1) return 0;
  const duration = clipDuration(frames);
  let acc = duration > 0 ? t % duration : 0;
  if (acc < 0) acc += duration; // guard negative t
  let j = 0;
  while (j < length - 1 && acc >= frames[j]!.delaySeconds) {
    acc -= frames[j]!.delaySeconds;
    j++;
  }
  return j;
}

/** The full output plan for a selection: grid + timing, all the deterministic bits. */
export interface MosaicPlan {
  readonly grid: MosaicGrid;
  /** Output duration in seconds: `loops` × the base (longest clip, or the static fallback). */
  readonly durationSeconds: number;
  /** Number of output frames at `fps`. */
  readonly frameCount: number;
  readonly fps: number;
  /** Whole loops of the base duration the output plays (≥ 1). */
  readonly loops: number;
}

/**
 * Compute the whole deterministic output plan from the per-cell durations and the
 * chosen layout — everything the encode loop needs except the pixels. `durations`
 * is one entry per selected cell (a clip's `clipDuration`, or 0 for a photo); its
 * length is the cell count that sizes the grid. `colsOverride` forwards the UI's
 * explicit column choice to `planGrid` (see there). `loops` is how many times the
 * base duration plays (the UI's length stepper; rounded, ≥ 1, default one loop —
 * the single repetition web/mosaic.js produced).
 */
export function planMosaic(
  durations: readonly number[],
  layout: MosaicLayout,
  fps: number = MOSAIC_FPS,
  colsOverride?: number,
  loops: number = 1,
): MosaicPlan {
  const grid = planGrid(durations.length, layout, colsOverride);
  const loopCount = Number.isFinite(loops) ? Math.max(1, Math.round(loops)) : 1;
  const durationSeconds = mosaicDuration(durations) * loopCount;
  return {
    grid,
    durationSeconds,
    frameCount: outputFrameCount(durationSeconds, fps),
    fps,
    loops: loopCount,
  };
}

/**
 * Download filename for a Mosaic, named by its actual grid shape (`mosaic-3x4.mp4`)
 * — honest about the produced video now that the column count is owner-chosen, not
 * just one of two fixed layouts (web/mosaic.js named it by layout).
 */
export function mosaicFilename(grid: Pick<MosaicGrid, "cols" | "rows">): string {
  return `mosaic-${grid.cols}x${grid.rows}.mp4`;
}
